<script setup>
import { watch, computed, onMounted, ref } from 'vue';
import ContactReasonTree from './ContactReasonTree.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContextMenu from './ContactReasonContextMenu.vue';

const props = defineProps({
  reason: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 1,
  },
  contactReasonPath: {
    type: Array,
    default: () => [],
  },
  selectedId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['selectContactReason', 'created']);

const contextMenuPosition = ref({});
const showContextMenu = ref(false);
const isOpen = ref(false);
const detailsRef = ref(null);

const hasChildren = computed(
  () =>
    Array.isArray(props.reason.childReasons) &&
    props.reason.childReasons.length > 0
);

const backgroundClass = computed(() => {
  if (props.selectedId === Number(props.reason.id)) {
    return 'bg-n-teal-10';
  }
  const level = props.level;
  const classMappings = {
    1: 'bg-n-blue-9',
    2: 'bg-n-blue-8',
    3: 'bg-n-blue-6',
  };
  return classMappings[level] || 'bg-n-blue-4';
});

const toggleDetails = event => {
  event.preventDefault();
  event.stopPropagation();
  if (detailsRef.value) {
    detailsRef.value.open = !detailsRef.value.open;
    isOpen.value = detailsRef.value.open;
  }
};

const onToggle = event => {
  isOpen.value = event.target.open;
};

const triggerSelectedReason = event => {
  event.preventDefault();
  event.stopPropagation();
  emit('selectContactReason', {
    reason: props.reason,
    path: props.contactReasonPath,
  });
};

const openContextMenu = event => {
  event.preventDefault();
  event.stopPropagation();
  contextMenuPosition.value = {
    x: event.pageX || event.clientX,
    y: event.pageY || event.clientY,
  };
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
};

const handleCreated = newContactReason => {
  emit('created', newContactReason);

  if (!newContactReason) return;
  const parentId =
    newContactReason.parent_id ?? newContactReason.parentId ?? null;

  if (parentId === props.reason.id) {
    if (!isOpen.value) {
      isOpen.value = true;
      if (detailsRef.value) {
        detailsRef.value.open = true;
      }
    }
  }
};

onMounted(() => {
  if (detailsRef.value) {
    isOpen.value = detailsRef.value.open || false;
  }
});

watch(
  () => props.selectedId,
  newVal => {
    if (newVal === props.reason.id && hasChildren.value) {
      isOpen.value = true;
      if (detailsRef.value) {
        detailsRef.value.open = true;
      }
    }
  }
);
</script>

<template>
  <li class="w-fit">
    <details
      v-if="hasChildren"
      ref="detailsRef"
      :open="isOpen"
      @toggle="onToggle"
      @click="toggleDetails"
    >
      <summary class="tree-node" @click.prevent>
        <div
          class="tree-node-content group flex items-center justify-between"
          :class="backgroundClass"
        >
          <div class="flex items-center">
            <span
              :style="{ backgroundColor: reason.config.color }"
              class="w-3 h-3 rounded-full me-2 inline-block"
            />
            <span @click.stop="triggerSelectedReason"> {{ reason.name }} </span>
          </div>
          <div class="flex items-center ms-4 gap-1">
            <button
              class="inline-flex items-center p-0.5 rounded hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              @click.stop="openContextMenu($event)"
            >
              <Icon icon="i-lucide-ellipsis" class="size-4" />
            </button>
            <Icon
              icon="i-lucide-chevron-right"
              class="size-5 transition-transform duration-200 shrink-0"
              :class="{ 'rotate-90': isOpen }"
              @click.stop="toggleDetails"
            />
          </div>
        </div>
      </summary>

      <ContactReasonTree
        :contact-reasons="reason.childReasons"
        :level="level + 1"
        :selected-id="selectedId"
        :contact-reason-path="[...props.contactReasonPath]"
        @select-contact-reason="emit('selectContactReason', $event)"
        @created="handleCreated"
      />
    </details>

    <div v-else class="tree-node">
      <div
        class="tree-node-content group flex items-center justify-between"
        :class="backgroundClass"
        @click="triggerSelectedReason"
      >
        <div class="flex items-center">
          <span
            :style="{ backgroundColor: reason.config.color }"
            class="w-3 h-3 rounded-full me-2 inline-block"
          />
          <span> {{ reason.name }} </span>
        </div>
        <button
          class="inline-flex items-center ms-4 p-0.5 rounded hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          @click.stop="openContextMenu($event)"
        >
          <Icon icon="i-lucide-ellipsis" class="size-4" />
        </button>
      </div>
    </div>
    <div class="context-menu-wrap">
      <ContextMenu
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :contact-reason="reason"
        :contact-reason-path="contactReasonPath"
        :level="level"
        @open="openContextMenu"
        @close="closeContextMenu"
        @created="handleCreated"
      />
    </div>
  </li>
</template>

<style lang="scss" scoped>
details {
  inline-size: 50ch;

  @media (prefers-reduced-motion: no-preference) {
    interpolate-size: allow-keywords;
  }

  &::details-content {
    opacity: 0;
    block-size: 0;
    overflow-y: clip;
    transition:
      content-visibility 0.6s allow-discrete,
      opacity 0.6s,
      block-size 0.6s;
  }

  &[open]::details-content {
    opacity: 1;
    block-size: auto;
  }
}

summary {
  cursor: pointer;
}

summary::marker {
  content: none;
}

.tree-node {
  @apply py-1;
}

.tree-node-content {
  @apply rounded text-sm font-medium p-2 w-fit cursor-pointer text-white;
  margin-left: 0.8em;

  span:hover {
    @apply underline;
  }
}
</style>
